import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { wiseAccountRequirementsService } from "../../lib/wiseAccountRequirementsService";
import Button from "./Button";
import Alert from "./Alert";
import { Loader2 } from "lucide-react";

/**
 * Dynamic Wise Form Component
 * Renders form fields based on Wise Account Requirements API response
 */
const DynamicWiseForm = ({
  sourceCurrency = "USD",
  targetCurrency,
  sourceAmount = 100,
  onSubmit,
  onCancel,
  submitting = false,
  initialData = {},
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requirements, setRequirements] = useState(null);
  const [formFields, setFormFields] = useState([]);
  const [selectedAccountType, setSelectedAccountType] = useState(null);

  // Preferred account type patterns by currency (most commonly used)
  // These are patterns to match against actual Wise API account types
  const preferredAccountTypes = useMemo(
    () => ({
      // North America
      USD: ["aba", "routing"], // ACH transfer (most common in US)
      CAD: ["canadian", "routing"], // Traditional Canadian bank account

      // Europe - IBAN is standard
      EUR: ["iban"],
      GBP: ["sort_code", "uk"],
      CHF: ["iban", "swiss"],
      NOK: ["iban", "norwegian"],
      SEK: ["iban", "swedish"],
      DKK: ["iban", "danish"],
      PLN: ["iban", "polish"],
      CZK: ["iban", "czech"],
      HUF: ["iban", "hungarian"],

      // Asia Pacific
      AUD: ["australian", "bsb"],
      NZD: ["new_zealand", "nz"],
      SGD: ["singapore"],
      HKD: ["hong_kong", "hk"],
      JPY: ["japanese", "japan"],
      INR: ["indian", "india"],

      // Middle East & Africa
      AED: ["emirates", "uae"],
      ZAR: ["south_african", "south_africa"],
      ILS: ["israeli", "israel"],
      EGP: ["egyptian", "egypt"],

      // South America
      BRL: ["brazilian", "brazil"],
      ARS: ["argentinian", "argentina"],
      CLP: ["chilean", "chile"],
      COP: ["colombian", "colombia"],
      PEN: ["peruvian", "peru"],
      UYU: ["uruguayan", "uruguay"],

      // Other
      PKR: ["pakistani", "pakistan"],
      BDT: ["bangladeshi", "bangladesh"],
      NGN: ["nigerian", "nigeria"],
      KES: ["kenyan", "kenya"],
      GHS: ["ghanaian", "ghana"],
      MAD: ["moroccan", "morocco"],
      OMR: ["omani", "oman"],
    }),
    []
  );

  // Map Wise field types to HTML input types
  const mapWiseFieldType = useCallback((wiseType) => {
    const typeMap = {
      text: "text",
      select: "select",
      radio: "radio",
      date: "date",
      email: "email",
      phone: "tel",
      number: "number",
      textarea: "textarea",
    };

    return typeMap[wiseType] || "text";
  }, []);

  // Enhance field with country-specific improvements
  const enhanceFieldForCountry = useCallback((field, targetCurrency) => {
    const fieldKey = field.key?.toLowerCase() || "";
    const fieldName = field.name?.toLowerCase() || "";

    // Country-specific placeholder improvements
    const countryEnhancements = {
      // India-specific
      INR: {
        pincode: { placeholder: "e.g., 110001", pattern: "^[1-9][0-9]{5}$" },
        pin_code: { placeholder: "e.g., 110001", pattern: "^[1-9][0-9]{5}$" },
        state: { placeholder: "e.g., Delhi, Maharashtra" },
      },
      // Japan-specific
      JPY: {
        postcode: {
          placeholder: "e.g., 100-0001",
          pattern: "^[0-9]{3}-[0-9]{4}$",
        },
        postal_code: {
          placeholder: "e.g., 100-0001",
          pattern: "^[0-9]{3}-[0-9]{4}$",
        },
        prefecture: { placeholder: "e.g., Tokyo, Osaka" },
      },
      // UK-specific
      GBP: {
        postcode: {
          placeholder: "e.g., SW1A 1AA",
          pattern: "^[A-Z]{1,2}[0-9R][0-9A-Z]? [0-9][A-Z]{2}$",
        },
        postal_code: {
          placeholder: "e.g., SW1A 1AA",
          pattern: "^[A-Z]{1,2}[0-9R][0-9A-Z]? [0-9][A-Z]{2}$",
        },
      },
      // US-specific
      USD: {
        zip: { placeholder: "e.g., 10001", pattern: "^[0-9]{5}(-[0-9]{4})?$" },
        zip_code: {
          placeholder: "e.g., 10001",
          pattern: "^[0-9]{5}(-[0-9]{4})?$",
        },
        state: { placeholder: "e.g., NY, CA" },
      },
      // Canada-specific
      CAD: {
        postal_code: {
          placeholder: "e.g., K1A 0A6",
          pattern: "^[A-Z][0-9][A-Z] [0-9][A-Z][0-9]$",
        },
        postcode: {
          placeholder: "e.g., K1A 0A6",
          pattern: "^[A-Z][0-9][A-Z] [0-9][A-Z][0-9]$",
        },
        province: { placeholder: "e.g., ON, BC" },
      },
      // Australia-specific
      AUD: {
        postcode: { placeholder: "e.g., 2000", pattern: "^[0-9]{4}$" },
        postal_code: { placeholder: "e.g., 2000", pattern: "^[0-9]{4}$" },
        state: { placeholder: "e.g., NSW, VIC" },
      },
    };

    const enhancements = countryEnhancements[targetCurrency];
    if (enhancements) {
      // Check each field key for enhancements
      Object.entries(enhancements).forEach(([key, enhancement]) => {
        if (fieldKey.includes(key) || fieldName.includes(key)) {
          if (enhancement.placeholder && !field.placeholder) {
            field.placeholder = enhancement.placeholder;
          }
          if (enhancement.pattern && !field.validation) {
            field.validation = enhancement.pattern;
          }
        }
      });
    }

    return field;
  }, []);

  // Parse Wise requirements into form fields
  const parseWiseRequirements = useCallback(
    (requirements) => {
      if (!requirements || !Array.isArray(requirements)) {
        console.warn("⚠️ No requirements found, using fallback");
        return [];
      }

      const fields = [];
      const seenFields = new Set(); // Track field names to prevent duplicates across all requirements

      requirements.forEach((requirement) => {
        // Each requirement has fields array with name and group
        if (requirement.fields && Array.isArray(requirement.fields)) {
          requirement.fields.forEach((fieldGroup) => {
            // Each field has a group array with the actual field definitions
            if (fieldGroup.group && Array.isArray(fieldGroup.group)) {
              fieldGroup.group.forEach((field) => {
                // Skip if we've already seen this field across all requirements
                if (seenFields.has(field.key)) {
                  console.log(
                    `⚠️ parseWiseRequirements: Skipping duplicate field: ${field.key}`
                  );
                  return;
                }
                seenFields.add(field.key);

                // Check if this is an email field and make it required
                const isEmailField =
                  field.type === "email" ||
                  field.key?.toLowerCase().includes("email") ||
                  field.name?.toLowerCase().includes("email") ||
                  field.name?.toLowerCase().includes("e-mail") ||
                  field.label?.toLowerCase().includes("email") ||
                  field.label?.toLowerCase().includes("e-mail");

                // Clean up field label - remove "(Optional)" text and improve country-specific labels
                let cleanLabel = field.name || field.key;
                if (cleanLabel && typeof cleanLabel === "string") {
                  const originalLabel = cleanLabel;

                  // Remove "(Optional)" text
                  cleanLabel = cleanLabel
                    .replace(/\s*\(Optional\)/gi, "")
                    .trim();

                  // Improve country-specific address labels
                  const labelMappings = {
                    "Post code": "Postal Code",
                    Postcode: "Postal Code",
                    "ZIP code": "ZIP Code",
                    "Zip code": "ZIP Code",
                    "Pin code": "PIN Code",
                    Pincode: "PIN Code",
                    "State/Province": "State/Province",
                    Prefecture: "Prefecture",
                    "Recipient address": "Address",
                    "Street address": "Street Address",
                    "Building name": "Building Name",
                    "Apartment number": "Apartment Number",
                  };

                  // Apply label mappings for better consistency
                  Object.entries(labelMappings).forEach(([from, to]) => {
                    if (cleanLabel.toLowerCase() === from.toLowerCase()) {
                      cleanLabel = to;
                    }
                  });

                  if (originalLabel !== cleanLabel) {
                    console.log(
                      `🧹 parseWiseRequirements: Cleaned label "${originalLabel}" → "${cleanLabel}"`
                    );
                  }
                }

                // Apply country-specific enhancements
                const enhancedField = enhanceFieldForCountry(
                  { ...field },
                  targetCurrency
                );

                const fieldConfig = {
                  name: field.key,
                  label: cleanLabel,
                  type: mapWiseFieldType(field.type),
                  required: isEmailField ? true : field.required || false, // Force email fields to be required
                  placeholder: enhancedField.placeholder || field.example || "",
                  validation:
                    enhancedField.validation || field.validationRegexp || null,
                  minLength: field.minLength || null,
                  maxLength: field.maxLength || null,
                  options:
                    field.valuesAllowed?.map((option) => ({
                      value: option.key,
                      label: option.name,
                    })) || null,
                  requirementType: requirement.type, // Track which requirement type this field belongs to
                  requirementTitle: requirement.title, // Track the requirement title
                };

                // Log when we make an email field required
                if (isEmailField && !field.required) {
                  console.log(
                    `📧 parseWiseRequirements: Making email field "${field.key}" required (was optional)`
                  );
                }

                fields.push(fieldConfig);
              });
            }
          });
        }
      });

      console.log(
        `✅ parseWiseRequirements: Generated ${fields.length} unique fields from ${requirements.length} requirements`
      );
      return fields;
    },
    [mapWiseFieldType, enhanceFieldForCountry, targetCurrency]
  );

  // Get available account types from requirements, prioritizing preferred types
  const accountTypes = useMemo(() => {
    if (!requirements) return [];

    const allTypes = requirements.map((req) => ({
      type: req.type,
      title: req.title,
      usageInfo: req.usageInfo,
    }));

    console.log(
      `🔍 Available account types for ${targetCurrency}:`,
      allTypes.map((t) => `${t.type} (${t.title})`)
    );

    // Get the preferred account type patterns for this currency
    const preferredPatterns = preferredAccountTypes[targetCurrency];
    console.log(
      `🎯 Looking for preferred patterns [${preferredPatterns?.join(", ")}] for ${targetCurrency}`
    );

    if (preferredPatterns && preferredPatterns.length > 0) {
      // Find the preferred type by matching patterns
      const preferred = allTypes.find((type) =>
        preferredPatterns.some(
          (pattern) =>
            type.type.toLowerCase().includes(pattern.toLowerCase()) ||
            type.title?.toLowerCase().includes(pattern.toLowerCase())
        )
      );

      if (preferred) {
        console.log(
          `✅ Found preferred account type for ${targetCurrency}: ${preferred.type} (${preferred.title})`
        );
        // Return only the preferred type to simplify the UI
        return [preferred];
      } else {
        console.log(
          `⚠️ No preferred patterns matched for ${targetCurrency}. Available types:`,
          allTypes.map((t) => `${t.type} (${t.title})`)
        );
        console.log(`🔍 Detailed pattern matching for ${targetCurrency}:`);
        allTypes.forEach((type) => {
          preferredPatterns.forEach((pattern) => {
            const typeMatch = type.type
              .toLowerCase()
              .includes(pattern.toLowerCase());
            const titleMatch = type.title
              ?.toLowerCase()
              .includes(pattern.toLowerCase());
            console.log(
              `  - Pattern "${pattern}" vs Type "${type.type}" (${type.title}): type=${typeMatch}, title=${titleMatch}`
            );
          });
        });
      }
    }

    // If no preferred type found or not available, return the first one only
    // This simplifies the UI by showing only one option instead of overwhelming users
    if (allTypes.length > 0) {
      console.log(
        `📝 Using first available account type for ${targetCurrency}: ${allTypes[0].type} (${allTypes[0].title}) - preferred patterns: ${preferredPatterns?.join(", ") || "none"}`
      );
      console.log(
        `🔍 Will use account type: ${allTypes[0].type} for field generation`
      );
      return [allTypes[0]];
    }

    console.log(`❌ No account types found for ${targetCurrency}`);
    return allTypes;
  }, [requirements, targetCurrency, preferredAccountTypes]);

  // Get fields for selected account type
  const getFieldsForAccountType = useCallback(
    (accountType) => {
      console.log(`🔍 getFieldsForAccountType called with: ${accountType}`);

      if (!requirements || !accountType) {
        console.log(
          `❌ Missing requirements or accountType: requirements=${!!requirements}, accountType=${accountType}`
        );
        return [];
      }

      const requirement = requirements.find((req) => req.type === accountType);
      console.log(
        `🔍 Found requirement for ${accountType}:`,
        requirement ? "YES" : "NO"
      );

      if (!requirement?.fields) {
        console.log(`❌ No fields found for requirement ${accountType}`);
        return [];
      }

      console.log(
        `🔍 Processing ${requirement.fields.length} field groups for ${accountType}`
      );

      const fields = [];
      const seenFields = new Set(); // Track field names to prevent duplicates

      requirement.fields.forEach((fieldGroup, groupIndex) => {
        console.log(`🔍 Processing field group ${groupIndex}:`, fieldGroup);

        if (fieldGroup.group && Array.isArray(fieldGroup.group)) {
          fieldGroup.group.forEach((field, fieldIndex) => {
            console.log(
              `🔍 Processing field ${fieldIndex} in group ${groupIndex}:`,
              field.key
            );

            // Skip if we've already seen this field
            if (seenFields.has(field.key)) {
              console.log(`⚠️ Skipping duplicate field: ${field.key}`);
              return;
            }
            seenFields.add(field.key);

            // Check if this is an email field and make it required
            const isEmailField =
              field.type === "email" ||
              field.key?.toLowerCase().includes("email") ||
              field.name?.toLowerCase().includes("email") ||
              field.name?.toLowerCase().includes("e-mail") ||
              field.label?.toLowerCase().includes("email") ||
              field.label?.toLowerCase().includes("e-mail");

            // Clean up field label - remove "(Optional)" text and improve country-specific labels
            let cleanLabel = field.name || field.key;
            if (cleanLabel && typeof cleanLabel === "string") {
              const originalLabel = cleanLabel;

              // Remove "(Optional)" text
              cleanLabel = cleanLabel.replace(/\s*\(Optional\)/gi, "").trim();

              // Improve country-specific address labels
              const labelMappings = {
                "Post code": "Postal Code",
                Postcode: "Postal Code",
                "ZIP code": "ZIP Code",
                "Zip code": "ZIP Code",
                "Pin code": "PIN Code",
                Pincode: "PIN Code",
                "State/Province": "State/Province",
                Prefecture: "Prefecture",
                "Recipient address": "Address",
                "Street address": "Street Address",
                "Building name": "Building Name",
                "Apartment number": "Apartment Number",
              };

              // Apply label mappings for better consistency
              Object.entries(labelMappings).forEach(([from, to]) => {
                if (cleanLabel.toLowerCase() === from.toLowerCase()) {
                  cleanLabel = to;
                }
              });

              if (originalLabel !== cleanLabel) {
                console.log(
                  `🧹 getFieldsForAccountType: Cleaned label "${originalLabel}" → "${cleanLabel}"`
                );
              }
            }

            // Apply country-specific enhancements
            const enhancedField = enhanceFieldForCountry(
              { ...field },
              targetCurrency
            );

            const fieldConfig = {
              name: field.key,
              label: cleanLabel,
              type: mapWiseFieldType(field.type),
              required: isEmailField ? true : field.required || false, // Force email fields to be required
              placeholder: enhancedField.placeholder || field.example || "",
              validation:
                enhancedField.validation || field.validationRegexp || null,
              minLength: field.minLength || null,
              maxLength: field.maxLength || null,
              options:
                field.valuesAllowed?.map((option) => ({
                  value: option.key,
                  label: option.name,
                })) || null,
              refreshRequirementsOnChange:
                field.refreshRequirementsOnChange || false,
              validationAsync: field.validationAsync || null,
            };

            // Log when we make an email field required
            if (isEmailField && !field.required) {
              console.log(
                `📧 Making email field "${field.key}" required (was optional)`
              );
            }

            fields.push(fieldConfig);
          });
        } else {
          console.log(
            `⚠️ Field group ${groupIndex} has no group array or is not an array`
          );
        }
      });

      console.log(
        `✅ Generated ${fields.length} unique fields for ${accountType}:`,
        fields.map((f) => f.name)
      );
      console.log(
        `🔍 Processed ${requirement.fields.length} field groups, found ${seenFields.size} unique fields`
      );
      return fields;
    },
    [requirements, mapWiseFieldType, enhanceFieldForCountry, targetCurrency]
  );

  // Group fields by category for selected account type
  const groupFields = useCallback((fields) => {
    const grouped = {
      personal: [],
      banking: [],
      address: [],
    };

    fields.forEach((field) => {
      const fieldName = field.name.toLowerCase();
      const fieldKey = field.key?.toLowerCase() || "";

      // Personal information fields
      if (
        fieldName.includes("name") ||
        fieldName.includes("email") ||
        fieldName.includes("phone") ||
        fieldName.includes("legaltype") ||
        fieldName.includes("recipient type") ||
        fieldKey.includes("name") ||
        fieldKey.includes("email") ||
        fieldKey.includes("phone")
      ) {
        grouped.personal.push(field);
      }
      // Address fields - more comprehensive and country-aware
      else if (
        fieldName.includes("address") ||
        fieldName.includes("city") ||
        fieldName.includes("country") ||
        fieldName.includes("postal") ||
        fieldName.includes("zip") ||
        fieldName.includes("state") ||
        fieldName.includes("postcode") ||
        fieldName.includes("pin code") || // India
        fieldName.includes("pincode") || // India
        fieldName.includes("prefecture") || // Japan
        fieldName.includes("province") || // Various countries
        fieldName.includes("region") ||
        fieldName.includes("district") ||
        fieldName.includes("locality") ||
        fieldName.includes("street") ||
        fieldName.includes("building") ||
        fieldName.includes("apartment") ||
        fieldName.includes("unit") ||
        fieldKey.includes("address") ||
        fieldKey.includes("city") ||
        fieldKey.includes("country") ||
        fieldKey.includes("postal") ||
        fieldKey.includes("zip") ||
        fieldKey.includes("state") ||
        fieldKey.includes("pin")
      ) {
        grouped.address.push(field);
      }
      // Banking/account fields (everything else)
      else {
        grouped.banking.push(field);
      }
    });

    // Log the grouping for debugging
    console.log(
      `🏠 Address fields grouped:`,
      grouped.address.map((f) => `${f.name} (${f.key})`)
    );
    console.log(
      `👤 Personal fields grouped:`,
      grouped.personal.map((f) => `${f.name} (${f.key})`)
    );
    console.log(
      `🏦 Banking fields grouped:`,
      grouped.banking.map((f) => `${f.name} (${f.key})`)
    );

    return grouped;
  }, []);

  // Get current form fields based on selected account type
  const currentFormFields = useMemo(() => {
    console.log(
      `🔍 currentFormFields: accountTypes.length=${accountTypes.length}, selectedAccountType=${selectedAccountType}`
    );

    if (accountTypes.length >= 1) {
      const validSelectedType = accountTypes.some(
        (accountType) => accountType.type === selectedAccountType
      )
        ? selectedAccountType
        : null;
      const typeToUse = validSelectedType || accountTypes[0].type;
      console.log(`🔍 Getting fields for account type: ${typeToUse}`);
      const fields = getFieldsForAccountType(typeToUse);
      console.log(
        `📝 Generated ${fields.length} fields for ${typeToUse}:`,
        fields.map((f) => f.name)
      );

      if (fields.length > 0) {
        return fields;
      }

      // Avoid mixing fields across account types if a stale selection slips through.
      if (typeToUse !== accountTypes[0].type) {
        const fallbackFields = getFieldsForAccountType(accountTypes[0].type);
        if (fallbackFields.length > 0) {
          console.log(
            `⚠️ No fields from account type ${typeToUse}, falling back to ${accountTypes[0].type}`
          );
          return fallbackFields;
        }
      }

      return [];
    }

    // Only use fallback if no account types are available at all
    console.log(
      `📝 No account types available, using fallback fields (${formFields.length} fields)`
    );
    return formFields;
  }, [selectedAccountType, formFields, getFieldsForAccountType, accountTypes]);

  const selectedFields = useMemo(() => {
    console.log(
      `🔍 selectedFields: Grouping ${currentFormFields.length} fields`
    );
    const grouped = groupFields(currentFormFields);
    console.log(`📝 selectedFields grouped:`, {
      personal: grouped.personal.length,
      banking: grouped.banking.length,
      address: grouped.address.length,
    });
    return grouped;
  }, [currentFormFields, groupFields]);

  const loadRequirements = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      console.log(
        `🔍 Loading requirements for ${sourceCurrency} → ${targetCurrency}`
      );

      const result =
        await wiseAccountRequirementsService.getAccountRequirements(
          sourceCurrency,
          targetCurrency,
          sourceAmount
        );

      if (result.success) {
        console.log(
          `🔍 Raw requirements data for ${targetCurrency}:`,
          result.data.requirements
        );
        setRequirements(result.data.requirements);

        // Parse requirements into form fields
        const fields = parseWiseRequirements(result.data.requirements);
        setFormFields(fields);

        console.log(
          `✅ Loaded ${fields.length} fields for ${targetCurrency}:`,
          fields.map((f) => f.name)
        );

        // Debug account types
        const types = result.data.requirements
          ? result.data.requirements.map((req) => ({
              type: req.type,
              title: req.title,
              usageInfo: req.usageInfo,
            }))
          : [];
        console.log(`🔍 Account types for ${targetCurrency}:`, types);
      } else {
        setError("Failed to load form requirements: " + result.error);
        console.error("❌ Requirements error:", result.error);
      }
    } catch (err) {
      setError("Failed to load form requirements: " + err.message);
      console.error("❌ Requirements exception:", err);
    } finally {
      setLoading(false);
    }
  }, [sourceCurrency, targetCurrency, sourceAmount, parseWiseRequirements]);

  // Create dynamic Zod schema based on current form fields
  const createZodSchema = useCallback((fields) => {
    if (!fields || fields.length === 0) {
      return z.object({});
    }

    const schemaFields = {};
    const nestedFields = {};

    fields.forEach((field) => {
      let fieldSchema = z.string();

      // Apply all validations to the base string schema first
      if (field.minLength) {
        fieldSchema = fieldSchema.min(
          field.minLength,
          `${field.label} must be at least ${field.minLength} characters`
        );
      }

      if (field.maxLength) {
        fieldSchema = fieldSchema.max(
          field.maxLength,
          `${field.label} must be at most ${field.maxLength} characters`
        );
      }

      if (field.validation) {
        try {
          fieldSchema = fieldSchema.regex(
            new RegExp(field.validation),
            `${field.label} format is invalid`
          );
        } catch (e) {
          console.warn(`⚠️ Invalid regex for ${field.name}:`, field.validation);
        }
      }

      if (field.type === "email") {
        fieldSchema = fieldSchema.email(`${field.label} must be a valid email`);
      }

      // Handle required vs optional after all other validations
      if (field.required) {
        fieldSchema = fieldSchema.min(1, `${field.label} is required`);
      } else {
        fieldSchema = fieldSchema.optional();
      }

      // Handle nested field names (e.g., address.country)
      if (field.name.includes(".")) {
        const [parentKey, childKey] = field.name.split(".");
        if (!nestedFields[parentKey]) {
          nestedFields[parentKey] = {};
        }
        nestedFields[parentKey][childKey] = fieldSchema;
      } else {
        schemaFields[field.name] = fieldSchema;
      }
    });

    // Add nested objects to schema
    Object.entries(nestedFields).forEach(([parentKey, childFields]) => {
      schemaFields[parentKey] = z.object(childFields);
    });

    console.log(
      "🔍 Created Zod schema with fields:",
      Object.keys(schemaFields)
    );
    console.log("🔍 Nested fields:", nestedFields);

    return z.object(schemaFields);
  }, []);

  const zodSchema = useMemo(
    () => createZodSchema(currentFormFields),
    [createZodSchema, currentFormFields]
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(zodSchema),
    defaultValues: initialData,
    shouldUnregister: true,
  });

  // Load requirements when currency changes
  useEffect(() => {
    if (targetCurrency) {
      loadRequirements();
    }
  }, [targetCurrency, loadRequirements]);

  // Keep the selected type aligned with the currently loaded requirements.
  useEffect(() => {
    if (accountTypes.length === 0) {
      if (selectedAccountType !== null) {
        setSelectedAccountType(null);
      }
      return;
    }

    const hasValidSelection = accountTypes.some(
      (accountType) => accountType.type === selectedAccountType
    );

    if (!hasValidSelection) {
      console.log(
        "🔄 Auto-selecting preferred account type:",
        accountTypes[0].type
      );
      setSelectedAccountType(accountTypes[0].type);
    }
  }, [accountTypes, selectedAccountType]);

  // Reset form when currency or initial data changes
  useEffect(() => {
    reset(initialData);
  }, [targetCurrency, initialData, reset]);

  const handleFormSubmit = (data) => {
    console.log("🔍 Form submission data:", data);
    console.log(
      "🔍 Current form fields:",
      currentFormFields.map((f) => ({ name: f.name, required: f.required }))
    );
    console.log("🔍 Form errors:", errors);

    // Determine the account type to use (now always available since we auto-select)
    const accountTypeToUse =
      selectedAccountType ||
      (accountTypes.length >= 1 ? accountTypes[0].type : null);

    // Add currency and account type information to the form data
    const submitData = {
      ...data,
      sourceCurrency,
      targetCurrency,
      sourceAmount,
      accountType: accountTypeToUse,
    };

    console.log("🚀 Final submit data:", submitData);
    onSubmit(submitData);
  };

  const renderField = (field) => {
    const fieldName = field.name;
    const error =
      errors[fieldName] ||
      (field.name.includes(".") &&
        errors[field.name.split(".")[0]]?.[field.name.split(".")[1]]);

    // Debug logging for field errors
    if (error) {
      console.log(`🔍 Field error for ${fieldName}:`, error);
    }
    const commonClasses =
      "w-full px-4 py-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-transparent";

    switch (field.type) {
      case "select":
        return (
          <div key={field.name} className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">
              {field.label}{" "}
              {field.required && <span className="text-red-400">*</span>}
            </label>
            <select {...register(field.name)} className={commonClasses}>
              <option value="">Select {field.label}</option>
              {field.options?.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                  className="bg-gray-800"
                >
                  {option.label}
                </option>
              ))}
            </select>
            {error && (
              <p className="text-red-400 text-xs mt-1">{error.message}</p>
            )}
          </div>
        );

      case "textarea":
        return (
          <div key={field.name} className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">
              {field.label}{" "}
              {field.required && <span className="text-red-400">*</span>}
            </label>
            <textarea
              {...register(field.name)}
              placeholder={field.placeholder}
              rows={3}
              className={commonClasses}
            />
            {error && (
              <p className="text-red-400 text-xs mt-1">{error.message}</p>
            )}
          </div>
        );

      case "radio":
        return (
          <div key={field.name} className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">
              {field.label}{" "}
              {field.required && <span className="text-red-400">*</span>}
            </label>
            <div className="space-y-2">
              {field.options?.map((option) => (
                <label
                  key={option.value}
                  className="flex items-center space-x-2"
                >
                  <input
                    {...register(field.name)}
                    type="radio"
                    value={option.value}
                    className="text-[#B8460E] focus:ring-[#B8460E]"
                  />
                  <span className="text-white">{option.label}</span>
                </label>
              ))}
            </div>
            {error && (
              <p className="text-red-400 text-xs mt-1">{error.message}</p>
            )}
          </div>
        );

      default:
        return (
          <div key={field.name} className="space-y-2">
            <label className="block text-sm font-medium text-gray-300">
              {field.label}{" "}
              {field.required && <span className="text-red-400">*</span>}
            </label>
            <input
              {...register(field.name)}
              type={field.type}
              placeholder={field.placeholder}
              className={commonClasses}
            />
            {error && (
              <p className="text-red-400 text-xs mt-1">{error.message}</p>
            )}
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="text-center">
          <Loader2 className="animate-spin h-8 w-8 text-[#B8460E] mx-auto mb-4" />
          <p className="text-gray-400">Loading form...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Alert type="error" message={error} />
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  if (!targetCurrency) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-400">Please select a target currency first.</p>
        <Button variant="secondary" onClick={onCancel} className="mt-4">
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(
        (data) => {
          console.log("✅ Form validation passed, submitting...");
          handleFormSubmit(data);
        },
        (errors) => {
          console.log("❌ Form validation failed:", errors);
        }
      )}
      className="space-y-6"
    >
      {/* Show form fields when we have fields to show */}
      {(() => {
        console.log(
          `🔍 Form display check: currentFormFields.length=${currentFormFields.length}`
        );
        return currentFormFields.length > 0;
      })() && (
        <>
          {/* Personal Information */}
          {selectedFields.personal.length > 0 && (
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-white border-b border-white/10 pb-2">
                Personal Information
              </h4>
              <div className="grid grid-cols-1 gap-4">
                {selectedFields.personal.map(renderField)}
              </div>
            </div>
          )}

          {/* Banking Information */}
          {selectedFields.banking.length > 0 && (
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-white border-b border-white/10 pb-2">
                Banking Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {selectedFields.banking.map(renderField)}
              </div>
            </div>
          )}

          {/* Address Information */}
          {selectedFields.address.length > 0 && (
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-white border-b border-white/10 pb-2">
                Address Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {selectedFields.address.map(renderField)}
              </div>
            </div>
          )}

          {/* All Fields (fallback if no grouping worked) */}
          {selectedFields.personal.length === 0 &&
            selectedFields.banking.length === 0 &&
            selectedFields.address.length === 0 &&
            currentFormFields.length > 0 && (
              <div className="space-y-4">
                <h4 className="text-lg font-medium text-white border-b border-white/10 pb-2">
                  Recipient Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {currentFormFields.map(renderField)}
                </div>
              </div>
            )}
        </>
      )}

      {/* Form Actions */}
      <div className="flex justify-end gap-3 pt-6 border-t border-white/10">
        <Button variant="secondary" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={submitting || currentFormFields.length === 0}
        >
          {submitting ? (
            <>
              <Loader2 className="animate-spin h-4 w-4 mr-2" />
              Creating...
            </>
          ) : (
            "Create Recipient"
          )}
        </Button>
      </div>
    </form>
  );
};

export default DynamicWiseForm;
